import React from 'react';
import { AnalyticsOverview } from '../../../types/analytics.types';

interface AnalyticsHeaderProps {
  overview: AnalyticsOverview | null;
  isLoading: boolean;
}

interface HeaderStatProps {
  label: string;
  value: number | null;
  accent: string;
  isLoading: boolean;
}

const formatNumber = (value: number | null): string =>
  value === null ? '—' : value.toLocaleString();

const HeaderStat: React.FC<HeaderStatProps> = ({ label, value, accent, isLoading }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-4 py-2 shadow-sm">
    <span className={`h-2.5 w-2.5 rounded-full ${accent}`} />
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-base font-semibold text-slate-900 tabular-nums">
        {isLoading && value === null ? (
          <span className="inline-block h-4 w-10 rounded bg-slate-100 animate-pulse" />
        ) : (
          formatNumber(value)
        )}
      </span>
    </div>
  </div>
);

export const AnalyticsHeader: React.FC<AnalyticsHeaderProps> = ({ overview, isLoading }) => {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Analytics</h1>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Admin Only
          </span>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          CR activity, trends, and per-user contribution over time.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <HeaderStat
          label="Total CRs"
          value={overview?.totalCRs ?? null}
          accent="bg-slate-900"
          isLoading={isLoading}
        />
        <HeaderStat
          label="Active Users"
          value={overview?.totalActiveUsers ?? null}
          accent="bg-violet-500"
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
